import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import ImpersonatorContext from 'contexts/ImpersonatorContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import Flash from 'components/Flash';
import Loading from 'components/Loading';
import captureException from 'services/captureException';
import UsersContext from '../../../contexts/UsersContext';
import userTypeCheckers from './UsersProvider/userTypeCheckers';
import { LOCAL_STORAGE_IMPERSONATING_USER_ID } from '../../../constants';
import getLocalStorageImpersonatorId from './getLocalStorageImpersonatorId';
import { useLocation, useNavigate } from 'react-router-dom';

type ImpersonationStatus = 'none' | 'checking' | 'valid' | 'stale';

// The 'storage' event only fires in OTHER tabs, so writes from this tab
// announce themselves with this one.
const IMPERSONATION_CHANGED_EVENT = 'impersonation-changed';

const subscribeToStoredImpersonation = (onChange: () => void) => {
  window.addEventListener('storage', onChange);
  window.addEventListener(IMPERSONATION_CHANGED_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(IMPERSONATION_CHANGED_EVENT, onChange);
  };
};

const setStoredImpersonation = (
  impersonation: { userId: string; accountId: string } | undefined,
) => {
  if (impersonation) {
    window.localStorage.setItem(
      LOCAL_STORAGE_IMPERSONATING_USER_ID,
      JSON.stringify(impersonation),
    );
  } else {
    window.localStorage.removeItem(LOCAL_STORAGE_IMPERSONATING_USER_ID);
  }
  window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
};

const ImpersonatorProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { allUsers, isLoading: isLoadingUsers } = useContext(UsersContext);
  const { selectedAccountId, selectedAccount } =
    useContext(AccountPickerContext);
  const navigate = useNavigate();
  const location = useLocation();
  // Follows localStorage, so starting or stopping "View app as" in another
  // tab on this account is reflected here too, matching the header the API
  // clients send (they read the same value per request).
  const getImpersonatorIdSnapshot = useCallback(
    () =>
      getLocalStorageImpersonatorId({ accountId: selectedAccountId }) ??
      undefined,
    [selectedAccountId],
  );
  const impersonatorId = useSyncExternalStore(
    subscribeToStoredImpersonation,
    getImpersonatorIdSnapshot,
  );
  const [viewingAppAs, setViewingAppAs] = useState<
    UserManagement.SignedUpUser | UserManagement.PendingUser | undefined
  >();
  const [isStaleEndedFlashVisible, setIsStaleEndedFlashVisible] =
    useState<boolean>(false);
  const [staleEndedAccountName, setStaleEndedAccountName] = useState<string>();

  // The stored view-as user is only applied on the account it was started
  // on, but that user may since have been removed from it (or a value
  // stored before accounts were recorded may be for another account). Then
  // every request is authorized against a user with no access here and the
  // app never loads. Check it against the account's users (Firestore,
  // unaffected by the header) before rendering anything that could make such
  // a request.
  const impersonationStatus = useMemo((): ImpersonationStatus => {
    if (!impersonatorId) {
      return 'none';
    }
    if (isLoadingUsers) {
      return 'checking';
    }
    const target = allUsers.find((u) => u.id === impersonatorId);
    if (!target || target.isDeleted) {
      return 'stale';
    }
    return 'valid';
  }, [allUsers, impersonatorId, isLoadingUsers]);

  useEffect(() => {
    if (impersonationStatus !== 'stale' || !impersonatorId) {
      return;
    }

    const error = new Error(
      `View app as user ${impersonatorId} is not a user on account ${selectedAccount.accountId}`,
    );
    error.name = 'View app as: ended stale session';
    captureException(error);

    setStoredImpersonation(undefined);
    setStaleEndedAccountName(selectedAccount.accountName);
    setIsStaleEndedFlashVisible(true);
  }, [impersonationStatus, impersonatorId, selectedAccount]);

  useEffect(() => {
    if (!impersonatorId) {
      setViewingAppAs(undefined);
      return;
    }

    const viewingAs = allUsers
      .filter(userTypeCheckers.isSignedUpOrPendingUser)
      .find((u) => u.id === impersonatorId);
    if (!viewingAs) {
      setViewingAppAs(undefined);
      return;
    }

    setViewingAppAs(viewingAs);
  }, [allUsers, impersonatorId]);

  const startImpersonationAs = useCallback(
    (userId: string) => {
      setStoredImpersonation({ userId, accountId: selectedAccountId });
      navigate(`/${location.search}`);
    },
    [location.search, navigate, selectedAccountId],
  );

  const stopImpersonation = useCallback(() => {
    setStoredImpersonation(undefined);
    navigate('/');
  }, [navigate]);

  if (impersonationStatus === 'checking' || impersonationStatus === 'stale') {
    return <Loading />;
  }

  return (
    <ImpersonatorContext.Provider
      value={{
        impersonatorId,
        startImpersonationAs,
        stopImpersonation,
        viewingAppAs,
      }}
    >
      <Flash
        isFlashVisible={isStaleEndedFlashVisible}
        setIsFlashVisible={setIsStaleEndedFlashVisible}
        type="info"
        message={`View app as was turned off: that user isn't on ${
          staleEndedAccountName ?? 'this account'
        }.`}
        // Dismissable, not timed: the splash screen can outlast the timeout
        isPermanent
      />
      {children}
    </ImpersonatorContext.Provider>
  );
};

export default ImpersonatorProvider;
